import React, { useState } from 'react';
import {
  History,
  ChevronRight,
  Trash2,
  FileText,
  CheckCircle2,
  PlayCircle,
  XCircle,
  Clock,
  Briefcase,
  Layers,
  ChevronDown,
  PauseCircle,
  CircleDashed,
  Zap,
  Play,
} from 'lucide-react';
import type { SmartStartSessionRow } from '../../types/smartStart';

interface Props {
  sessions: SmartStartSessionRow[];
  onOpen: (id: number) => void;
  onDelete: (id: number) => void;
}

const formatTimestamp = (raw: string | null | undefined): string => {
  if (!raw) return '';
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
  return d.toLocaleDateString();
};

interface StatusConfigEntry {
  label: string;
  color: string;
  dot: string;
  icon: React.ElementType;
  iconColor: string;
  description: string;
}

// Static icons only — animated indicators would mislead users into thinking
// a background process is running for that session row.
const statusConfig: Record<string, StatusConfigEntry> = {
  completed: {
    label: 'Completed',
    color: 'text-emerald-700',
    dot: 'bg-emerald-500',
    icon: CheckCircle2,
    iconColor: 'text-emerald-600',
    description: 'All steps in this session were completed.',
  },
  active: {
    label: 'In progress',
    color: 'text-lavender',
    dot: 'bg-lavender',
    icon: PlayCircle,
    iconColor: 'text-lavender',
    description: 'You started this session and have steps left to finish. Click to resume.',
  },
  awaiting_pipeline: {
    label: 'Awaiting pipeline',
    color: 'text-amber-700',
    dot: 'bg-amber-500',
    icon: PauseCircle,
    iconColor: 'text-amber-600',
    description: 'Goal was captured but no pipeline has been picked yet — open to choose one.',
  },
  awaiting_review: {
    label: 'Awaiting review',
    color: 'text-amber-700',
    dot: 'bg-amber-500',
    icon: Clock,
    iconColor: 'text-amber-600',
    description: 'A step finished and is waiting for your review or approval.',
  },
  failed: {
    label: 'Failed',
    color: 'text-red-700',
    dot: 'bg-red-500',
    icon: XCircle,
    iconColor: 'text-red-600',
    description: 'A step in this session failed and could not be recovered.',
  },
  aborted: {
    label: 'Aborted',
    color: 'text-gray-500',
    dot: 'bg-gray-400',
    icon: XCircle,
    iconColor: 'text-gray-500',
    description: 'This session was abandoned before it could finish.',
  },
  draft: {
    label: 'Draft',
    color: 'text-gray-600',
    dot: 'bg-gray-400',
    icon: CircleDashed,
    iconColor: 'text-gray-500',
    description: 'Goal was saved but the session has not been classified yet.',
  },
};

const SessionHistory: React.FC<Props> = ({ sessions, onOpen, onDelete }) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  if (sessions.length === 0) return null;

  const toggleExpand = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <History className="w-4 h-4 text-gray-600" />
        <h2 className="text-sm font-bold text-gray-700 uppercase tracking-wide">
          Recent sessions
        </h2>
        <span className="text-xs text-gray-400">({sessions.length})</span>
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="max-h-[60vh] overflow-y-auto divide-y divide-gray-100">
          {sessions.map((s) => {
            const cfg = statusConfig[s.status] || {
              label: s.status.replace(/_/g, ' '),
              color: 'text-amber-700',
              dot: 'bg-amber-500',
              icon: Clock,
              iconColor: 'text-amber-600',
              description: `Session status: ${s.status}`,
            };
            const StatusIcon = cfg.icon;
            const isExpanded = expandedId === s.id;

            const summary = s.summary || {
              document_count: 0,
              document_names: [],
              steps_total: 0,
              steps_completed: 0,
              steps_rejected: 0,
              current_step_index: null,
              current_step_label: null,
              project_name: null,
              pipeline_name: null,
              pipeline_key: null,
              direct_launch: false,
            };

            const stepProgressPct =
              summary.steps_total > 0
                ? Math.round((summary.steps_completed / summary.steps_total) * 100)
                : 0;

            const isResumable = s.status === 'active' || s.status === 'awaiting_review';
            const showResumeCta =
              isResumable && summary.current_step_label && summary.current_step_index != null;

            return (
              <div key={s.id} className="bg-white">
                <div
                  className="group flex items-start gap-3 p-3 hover:bg-lavender/5 transition cursor-pointer"
                  onClick={() => toggleExpand(s.id)}
                >
                  <div
                    className="flex flex-col items-center gap-1 mt-0.5"
                    title={cfg.description}
                  >
                    <StatusIcon className={`w-4 h-4 ${cfg.iconColor}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`text-[10px] font-bold uppercase tracking-wide ${cfg.color}`}
                        title={cfg.description}
                      >
                        {cfg.label}
                      </span>
                      {summary.direct_launch && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5"
                          title="Started in 1 click — pipeline was launched directly without typing a goal."
                        >
                          <Zap className="w-2.5 h-2.5" />
                          1-click
                        </span>
                      )}
                      <span
                        className="text-[10px] text-gray-400"
                        title={`Last updated ${
                          s.updated_at ? new Date(s.updated_at).toLocaleString() : 'unknown'
                        }`}
                      >
                        {formatTimestamp(s.updated_at) || formatTimestamp(s.created_at)}
                      </span>
                    </div>

                    <div
                      className="text-xs text-gray-900 font-medium line-clamp-2 leading-snug mb-1.5"
                      title={s.user_goal || s.title || ''}
                    >
                      {s.title || s.user_goal}
                    </div>

                    {showResumeCta && (
                      <div
                        className="text-[11px] text-lavender-deep mb-1.5 flex items-center gap-1.5"
                        title={`Click to resume at step ${
                          (summary.current_step_index ?? 0) + 1
                        } of ${summary.steps_total}: ${summary.current_step_label}`}
                      >
                        <Play className="w-3 h-3 fill-current flex-shrink-0" />
                        <span className="truncate">
                          Resume at step {(summary.current_step_index ?? 0) + 1}:{' '}
                          <span className="font-semibold">{summary.current_step_label}</span>
                        </span>
                      </div>
                    )}

                    <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                      {summary.project_name && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5"
                          title={`Project: ${summary.project_name} — every artifact this session creates lives inside this project.`}
                        >
                          <Briefcase className="w-2.5 h-2.5" />
                          <span className="truncate max-w-[140px]">{summary.project_name}</span>
                        </span>
                      )}
                      {summary.pipeline_name && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] text-lavender bg-lavender/10 border border-lavender/30 rounded px-1.5 py-0.5"
                          title={`Pipeline: ${summary.pipeline_name} — the workflow Smart Router routed this goal into.`}
                        >
                          <Layers className="w-2.5 h-2.5" />
                          <span className="truncate max-w-[140px]">{summary.pipeline_name}</span>
                        </span>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2">
                      {summary.steps_total > 0 && (
                        <div
                          className="flex items-center gap-1.5"
                          title={`${summary.steps_completed} of ${summary.steps_total} plan steps completed (${stepProgressPct}%)`}
                        >
                          <div className="flex items-center text-[10px] text-gray-600">
                            <span className="font-semibold">
                              {summary.steps_completed}/{summary.steps_total}
                            </span>
                            <span className="ml-1">steps</span>
                          </div>
                          <div className="w-16 h-1 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${
                                stepProgressPct === 100 ? 'bg-emerald-500' : 'bg-lavender'
                              }`}
                              style={{ width: `${stepProgressPct}%` }}
                            />
                          </div>
                        </div>
                      )}
                      {summary.document_count > 0 && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] text-sky-800 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5"
                          title={
                            summary.document_names.length > 0
                              ? `Documents attached to this session:\n• ${summary.document_names.slice(0, 10).join('\n• ')}${
                                  summary.document_names.length > 10
                                    ? `\n+ ${summary.document_names.length - 10} more`
                                    : ''
                                }`
                              : `${summary.document_count} documents attached to this session`
                          }
                        >
                          <FileText className="w-2.5 h-2.5" />
                          {summary.document_count} doc{summary.document_count === 1 ? '' : 's'}
                        </span>
                      )}
                      {summary.steps_rejected > 0 && (
                        <span
                          className="inline-flex items-center gap-1 text-[10px] text-red-700 bg-red-50 border border-red-200 rounded px-1.5 py-0.5"
                          title={`${summary.steps_rejected} step${
                            summary.steps_rejected === 1 ? ' was' : 's were'
                          } rejected and need to be retried or skipped.`}
                        >
                          <XCircle className="w-2.5 h-2.5" />
                          {summary.steps_rejected} rejected
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    {isResumable ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(s.id);
                        }}
                        title={
                          showResumeCta
                            ? `Resume at step ${(summary.current_step_index ?? 0) + 1}: ${summary.current_step_label}`
                            : 'Resume this session'
                        }
                        className="flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold bg-lavender text-white rounded-md hover:bg-lavender-deep transition"
                      >
                        <Play className="w-3 h-3 fill-current" />
                        Resume
                      </button>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpen(s.id);
                        }}
                        title="Open session"
                        className="flex items-center gap-1 px-2 py-1 text-[11px] text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 hover:text-gray-800 transition"
                      >
                        Open
                        <ChevronRight className="w-3 h-3" />
                      </button>
                    )}
                    <div className="flex items-center gap-1 text-gray-400">
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleExpand(s.id); }}
                        className="p-0.5 hover:text-gray-700"
                        title={isExpanded ? 'Collapse' : 'Expand'}
                      >
                        <ChevronDown
                          className={`w-3.5 h-3.5 transition-transform ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(s.id);
                        }}
                        className="p-0.5 opacity-0 group-hover:opacity-100 hover:text-red-600 transition"
                        title="Delete session"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>

                {isExpanded && (
                  <div className="px-3 pb-3 -mt-1 bg-gradient-to-b from-lavender/5 to-white border-t border-lavender/10">
                    {summary.document_count > 0 ? (
                      <div className="mt-2">
                        <div className="text-[10px] uppercase tracking-wide font-bold text-gray-500 mb-1.5 flex items-center gap-1">
                          <FileText className="w-3 h-3 text-sky-600" />
                          Documents in this session
                        </div>
                        <ul className="space-y-1">
                          {summary.document_names.slice(0, 8).map((name, i) => (
                            <li
                              key={`${name}-${i}`}
                              className="flex items-center gap-1.5 text-[11px] text-gray-700 px-2 py-1 bg-white border border-sky-100 rounded"
                            >
                              <FileText className="w-3 h-3 text-sky-500 flex-shrink-0" />
                              <span className="truncate">{name}</span>
                            </li>
                          ))}
                          {summary.document_names.length > 8 && (
                            <li className="text-[10px] text-gray-500 italic px-2">
                              + {summary.document_names.length - 8} more
                            </li>
                          )}
                        </ul>
                      </div>
                    ) : (
                      <div className="mt-2 text-[11px] text-gray-500 italic px-2 py-1.5 bg-gray-50 rounded">
                        No documents were attached to this session.
                      </div>
                    )}

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onOpen(s.id);
                      }}
                      className="mt-3 w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-semibold bg-lavender text-white rounded-lg hover:bg-lavender-deep transition"
                    >
                      Open full session
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default SessionHistory;
